import React, { useEffect } from 'react'
import { Box } from '@mui/material'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Underline from '@tiptap/extension-underline'
import { TextStyle, Color, BackgroundColor } from '@tiptap/extension-text-style'
import Highlight from '@tiptap/extension-highlight'
import { INTERAX_COLORS } from '../../../../../constants/colors'
import type { SprintEmailContent } from '../../../../../../../pure-js/types/Sprint'
import { EmailEditorToolbar } from './EmailEditorToolbar'
import {
  VariableChipExtension,
  ActivitiesBlockExtension,
  deserializeToEditorHtml,
  serializeEditorHtml
} from './VariableChipExtension'
import { useEmailEditorState } from './useEmailEditorState'

interface EmailEditorProps {
  initialContent?: SprintEmailContent
  onContentChange: (content: SprintEmailContent) => void
  editorStateRef?: React.MutableRefObject<{ resetToDefaults: () => void } | null>
}

export const EmailEditor: React.FC<EmailEditorProps> = ({ initialContent, onContentChange, editorStateRef }) => {
  const { content, updateBody, resetToDefaults } = useEmailEditorState({ initialContent })

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        code: false
      }),
      Link.configure({ openOnClick: false }),
      Underline,
      TextStyle,
      Color,
      BackgroundColor,
      Highlight.configure({ multicolor: true }),
      VariableChipExtension,
      ActivitiesBlockExtension
    ],
    content: deserializeToEditorHtml(content.body),
    onUpdate: ({ editor: updatedEditor }) => {
      const html = serializeEditorHtml(updatedEditor.getHTML())
      updateBody(html)
    },
    editorProps: {
      attributes: {
        style: `color: ${INTERAX_COLORS.typographyText}; outline: none; min-height: 300px; padding: 12px; font-size: 14px; line-height: 1.6;`
      },
      handlePaste: (view, event) => {
        const text = event.clipboardData?.getData('text/plain')
        if (text) {
          event.preventDefault()
          const { state, dispatch } = view
          dispatch(state.tr.insertText(text))
          return true
        }
        return false
      }
    }
  })

  // Expose resetToDefaults to parent
  useEffect(() => {
    if (editorStateRef) {
      editorStateRef.current = { resetToDefaults }
    }
  }, [editorStateRef, resetToDefaults])

  // Notify parent on content changes
  useEffect(() => {
    onContentChange(content)
  }, [content]) // eslint-disable-line react-hooks/exhaustive-deps

  // Sync content from outside (e.g. reset to default)
  useEffect(() => {
    if (!editor) return
    const currentSerialized = serializeEditorHtml(editor.getHTML())
    if (currentSerialized !== content.body) {
      editor.commands.setContent(deserializeToEditorHtml(content.body))
    }
  }, [content.body]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box
      sx={{
        border: 1,
        borderColor: INTERAX_COLORS.heritageBlue,
        borderRadius: 1,
        overflow: 'hidden'
      }}
    >
      <EmailEditorToolbar editor={editor} />
      <Box
        sx={{
          bgcolor: INTERAX_COLORS.heritageBlue,
          '& .ProseMirror': {
            outline: 'none',
            '& p': { margin: '0 0 8px 0' },
            '& ul': {
              listStyleType: 'disc',
              paddingLeft: '24px',
              margin: '0 0 8px 0'
            },
            '& ol': {
              listStyleType: 'decimal',
              paddingLeft: '24px',
              margin: '0 0 8px 0'
            },
            '& li': {
              display: 'list-item',
              marginBottom: '4px'
            },
            '& a': { color: INTERAX_COLORS.sun }
          }
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  )
}
